/**
 * TerminatedPage (V72)
 *
 * 퇴사 처리된 사용자에게 표시되는 페이지입니다.
 * - users.status === 'terminated' 인 경우
 * - users.leaveDate < today(KST) 인 경우
 *
 * 자동으로 로그아웃 처리되며, 재로그인 시도 시 Firebase Auth에서 차단됩니다.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { UserX, LogOut } from 'lucide-react';
import { auth } from '../../firebase/config';

interface TerminatedPageProps {
  /** 퇴사일 (YYYY-MM-DD) */
  leaveDate?: string;
  /** 직원 이름 */
  displayName?: string;
}

export function TerminatedPage({ leaveDate, displayName }: TerminatedPageProps) {
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);
  // V73: ref로 중복 실행 방지 (stale closure 문제 해결)
  const isSigningOutRef = useRef(false);

  // V73: useCallback으로 안정적인 함수 참조 유지
  const handleLogout = useCallback(async () => {
    // ref 기반 중복 실행 방지
    if (isSigningOutRef.current) return;
    isSigningOutRef.current = true;
    setIsSigningOut(true);

    try {
      if (auth) {
        await signOut(auth);
      }
      navigate('/login');
    } catch (err) {
      console.error('로그아웃 실패:', err);
      // 에러 발생해도 로그인 페이지로 이동
      navigate('/login');
    }
  }, [navigate]);

  // V72/V73: 페이지 로드 시 자동 로그아웃 (5초 후)
  useEffect(() => {
    const timer = setTimeout(() => {
      handleLogout();
    }, 5000);

    return () => clearTimeout(timer);
  }, [handleLogout]);

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* 아이콘 */}
        <div style={iconContainerStyle}>
          <UserX size={64} color="#9ca3af" />
        </div>

        {/* 메인 메시지 */}
        <h1 style={titleStyle}>퇴사 처리된 계정입니다</h1>
        <p style={messageStyle}>
          {displayName ? `${displayName}님, ` : ''}
          {leaveDate
            ? `${leaveDate} 기준으로 퇴사 처리되었습니다.`
            : '퇴사 처리되어 시스템에 접근할 수 없습니다.'}
        </p>

        {/* 부가 안내 */}
        <p style={subMessageStyle}>
          문의사항이 있으시면 인사팀에 연락해 주세요.
        </p>

        {/* 자동 로그아웃 안내 */}
        <p style={autoLogoutStyle}>
          5초 후 자동으로 로그아웃됩니다...
        </p>

        {/* 로그아웃 버튼 */}
        <button
          onClick={handleLogout}
          disabled={isSigningOut}
          style={{
            ...buttonStyle,
            opacity: isSigningOut ? 0.6 : 1,
            cursor: isSigningOut ? 'not-allowed' : 'pointer',
          }}
        >
          <LogOut size={18} />
          {isSigningOut ? '로그아웃 중...' : '지금 로그아웃'}
        </button>
      </div>
    </div>
  );
}

// 스타일
const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f3f4f6',
  padding: '20px',
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  padding: '48px 40px',
  maxWidth: '420px',
  width: '100%',
  textAlign: 'center',
};

const iconContainerStyle: React.CSSProperties = {
  marginBottom: '24px',
};

const titleStyle: React.CSSProperties = {
  fontSize: '22px',
  fontWeight: '700',
  color: '#1f2937',
  margin: '0 0 12px 0',
};

const messageStyle: React.CSSProperties = {
  fontSize: '16px',
  color: '#4b5563',
  margin: '0 0 8px 0',
};

const subMessageStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#9ca3af',
  margin: '0 0 16px 0',
};

const autoLogoutStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#f59e0b',
  margin: '0 0 24px 0',
  fontStyle: 'italic',
};

const buttonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 24px',
  fontSize: '15px',
  fontWeight: '500',
  color: '#6b7280',
  backgroundColor: 'white',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  cursor: 'pointer',
};
